import React from 'react';
import ReactDOM from 'react-dom/client';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import '@fontsource-variable/nunito-sans';
import { Helmet, HelmetProvider } from 'react-helmet-async';

import { GoogleOAuthProvider } from '@react-oauth/google';
import { RecoilRoot } from 'recoil';
import { QueryClient } from '@tanstack/react-query';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { ThemeProvider } from '@mui/material';

import {
  AppLocalizationProvider,
  AuthProvider,
  LanguageProvider,
  SubscriptionProvider,
} from './data-access';
import './i18n';
import './index.scss';
import reportWebVitals from './reportWebVitals';
import { AppRouter } from './routing';
import theme from './theme';
import { FeatureFeedbackDialog, GeneralSnackBar } from './components';
import { dayjs } from './utils';
import { safeLocalStorage } from './utils/safeLocalStorage';

const MAX_AGE_OF_QUERY_CACHE = 1000 * 60 * 60 * 24 * 7; // 1 week
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: MAX_AGE_OF_QUERY_CACHE, // cache time till it's discarded,
    },
  },
});
const persister = createAsyncStoragePersister({
  storage: safeLocalStorage,
  throttleTime: 3000,
});

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
root.render(
  <React.StrictMode>
    <HelmetProvider>
      <Helmet>
        <title>Lifestack</title>
        <meta
          name="description"
          content="Lifestack helps you schedule better with your energy in mind"
        />
        <meta property="og:title" content="Lifestack" />
        <meta
          property="og:description"
          content="Lifestack helps you schedule better with your energy in mind"
        />
        <script defer>
          {`
              (function(w, r) {
                w._rwq = r;
                w[r] = w[r] || function() {
                  (w[r].q = w[r].q || []).push(arguments);
                };
              })(window, 'rewardful');
            `}
        </script>
        <script async src="https://r.wdfl.co/rw.js" data-rewardful="cdb92b" defer></script>
      </Helmet>
      <PersistQueryClientProvider client={queryClient} persistOptions={{ persister }}>
        <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID!}>
          <RecoilRoot>
            <AuthProvider>
              <LanguageProvider>
                <SubscriptionProvider>
                  <AppLocalizationProvider>
                    <ThemeProvider theme={theme}>
                      <AppRouter />
                      <FeatureFeedbackDialog />
                      <GeneralSnackBar />
                    </ThemeProvider>
                  </AppLocalizationProvider>
                </SubscriptionProvider>
              </LanguageProvider>
            </AuthProvider>
          </RecoilRoot>
        </GoogleOAuthProvider>
      </PersistQueryClientProvider>
    </HelmetProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
